"use client";

import { safeNextPath } from "@/lib/auth/safe-next";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { classifyAuthError } from "@/lib/supabase/auth-error-copy";
import { GoogleIcon } from "./OAuthIcons";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            ux_mode?: "popup" | "redirect";
            auto_select?: boolean;
            itp_support?: boolean;
          }) => void;
          renderButton: (
            el: HTMLElement,
            cfg: {
              type?: "standard" | "icon";
              theme?: "outline" | "filled_blue" | "filled_black";
              size?: "large" | "medium" | "small";
              text?: "signin_with" | "signup_with" | "continue_with" | "signin";
              shape?: "rectangular" | "pill" | "circle" | "square";
              logo_alignment?: "left" | "center";
              width?: number | string;
              locale?: string;
            },
          ) => void;
          prompt: () => void;
        };
      };
    };
  }
}

const GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

interface GoogleGsiButtonProps {
  intent?: "signup" | "login";
  next?: string | null;
}

/**
 * Renders Google's official Sign-in button via the GSI client SDK
 * (https://accounts.google.com/gsi/client). Returns an ID token via the
 * callback, which we exchange for a Supabase session via signInWithIdToken.
 *
 * Falls back to a hand-styled button matching Google's brand spec when
 * NEXT_PUBLIC_GOOGLE_CLIENT_ID isn't set.
 */
export function GoogleGsiButton({ intent = "login", next }: GoogleGsiButtonProps) {
  const router = useRouter();
  const slotRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
  const verb = intent === "signup" ? "Sign up" : "Sign in";

  useEffect(() => {
    if (!clientId || !slotRef.current) return;

    let cancelled = false;
    const slot = slotRef.current;

    const init = () => {
      if (cancelled || !window.google?.accounts?.id || !slot) return;
      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            const supabase = createClient();
            const { error } = await supabase.auth.signInWithIdToken({
              provider: "google",
              token: response.credential,
            });
            if (error) {
              console.error("Google sign-in error", error);
              const { display } = classifyAuthError(error, "oauth-google");
              setError(display);
              return;
            }
            // No refresh after it: it would hold the sign-in until a second
            // fetch of the same page lands (MUCKA-WEB-CQ, see /login).
            router.push(safeNextPath(next));
          },
          itp_support: true,
        });
        const width = Math.min(slot.clientWidth || 320, 400);
        slot.innerHTML = "";
        window.google.accounts.id.renderButton(slot, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: intent === "signup" ? "signup_with" : "signin_with",
          shape: "rectangular",
          // 'center' groups logo + text together (matches Apple's button)
          // instead of pinning the logo flush to the left edge.
          logo_alignment: "center",
          width,
        });
      } catch (e) {
        console.error("GSI render error", e);
      }
    };

    const existing = document.querySelector(`script[src="${GSI_SCRIPT_SRC}"]`);
    if (existing) {
      // Script may still be loading; defer one tick so window.google is ready.
      if (window.google?.accounts?.id) init();
      else existing.addEventListener("load", init, { once: true });
    } else {
      const s = document.createElement("script");
      s.src = GSI_SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.addEventListener("load", init, { once: true });
      document.head.appendChild(s);
    }

    return () => {
      cancelled = true;
    };
  }, [clientId, intent, next, router]);

  // Fallback: hand-styled button matching the official spec when no client_id is configured.
  if (!clientId) {
    return (
      <button
        type="button"
        className="mucka-google-btn"
        onClick={async () => {
          const supabase = createClient();
          await supabase.auth.signInWithOAuth({
            provider: "google",
            options: {
              redirectTo: `${window.location.origin}/auth/callback${
                next ? `?next=${encodeURIComponent(next)}` : ""
              }`,
            },
          });
        }}
      >
        <GoogleIcon />
        <span>{verb} with Google</span>
      </button>
    );
  }

  return (
    <>
      <div ref={slotRef} className="mucka-gsi-slot" />
      {error && <div className="mucka-auth-field-error">{error}</div>}
    </>
  );
}
